<template lang="html">
  <div>
    <b-row>
      <b-col class="mt-5">
        <div :class="{ 'alert alert-info': highlight == 'title' }">
          <div>
            <h3>Aspect Ratio</h3>
            <p>Choose an aspect ratio for your show.</p>
            <b-form-select
              v-model="aspectRatioSelected"
              :options="aspectRatioOptions"
            />
          </div>
          <div class="mt-4">
            <h4>Colors</h4>
            <b-row>
              <b-col>
                <b-form-group
                  id="input-group-color-2"
                  label="Background color"
                  label-for="input-color-2"
                  :invalid-feedback="
                    `'${globalBackgroundColor}' is not a valid color.`
                  "
                  description="The color that appears around your show player online."
                  class="mt-2"
                >
                  <b-form-input
                    id="input-color-2"
                    v-model="globalBackgroundColor"
                    type="text"
                    :state="validateColor(globalBackgroundColor)"
                    required
                    placeholder="#111111"
                  />
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  id="input-group-color-1"
                  label="Slide background color"
                  label-for="input-color-1"
                  :invalid-feedback="
                    `'${globalSlideBackgroundColor}' is not a valid color.`
                  "
                  description="The color that appears behind your slides."
                  class="mt-2"
                >
                  <b-form-input
                    id="input-color-1"
                    v-model="globalSlideBackgroundColor"
                    type="text"
                    :state="validateColor(globalSlideBackgroundColor)"
                    invalid-feedback="What?"
                    required
                    placeholder="#000000"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </div>
        <div
          :class="{ 'alert alert-info': highlight == 'privacy' }"
          class="mt-4"
        >
          <h3>Privacy</h3>
          <p>Select who can (or can not) see your show.</p>
          <p v-if="!isPublishable" class="alert alert-warning mt-2">
            <v-icon name="exclamation-triangle" /> This show contains features
            which can only be exported to video. Privacy options are limited to
            private view only.
          </p>
          <b-form-select
            v-model="privacySelected"
            :options="privacyOptions"
            :disabled="!isPublishable"
            size="lg"
          />

          <b-form-group
            v-if="showPasswordForm"
            id="input-group-3"
            label="Password"
            label-for="input-3"
            description="The password of your show. (required)"
            class="mt-3"
          >
            <b-form-input
              id="input-3"
              v-model="password"
              type="text"
              required
              placeholder="Enter password"
            />
          </b-form-group>
        </div>
        <div v-show="unsavedChanges" class="mt-2">
          <b-btn variant="primary" size="md" @click="savePassword">
            Save password
          </b-btn>
          <span class="alert alert-warning small ml-2">
            You have unsaved changes
          </span>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { Show } from "@/models/showModel";
import tinycolor from "tinycolor2";
export default {
  name: "ShowSettings",
  components: {},
  data() {
    return {
      password: "",
      showPasswordForm: false,
      globalSlideBackgroundColor: "#000000",
      globalBackgroundColor: "#000000",
      privacyOptions: [
        { value: "private", text: "Only I can view this show" },
        { value: "world", text: "Anyone can view this show" },
        { value: "password", text: "Anyone with the password" },
        { value: "privateLink", text: "Anyone with the private link" }
      ],
      aspectRatioOptions: [
        { value: 1.5, text: "3:2 - Still camera default" },
        { value: 1.777777778, text: "16:9 - Video camera default" },
        { value: 1.33333, text: "4:3 - Presentation default" },
        { value: 1, text: "1:1 - Square image" },
        { value: 0.5625, text: "9:16 - Vertical video" },
        { value: 0.8, text: "4:5 - Vertical image" }
      ]
    };
  },
  computed: {
    show() {
      return this.$store.state.show.currentShow;
    },
    readOnlyShowModel() {
      if (this.show) {
        return new Show(this.show);
      }
      return {};
    },
    isPublishable() {
      return this.readOnlyShowModel.isPublishable;
    },
    highlight() {
      return this.$route.params.highlight;
    },
    aspectRatioSelected: {
      get() {
        var displaySettings = this.show.data.displaySettings || {};
        return displaySettings.aspectRatio || 1.5;
      },
      set(value) {
        console.log("aspectRatioSelected", value, this.aspectRatioSelected);
        if (value != this.aspectRatioSelected) {
          this.$store.dispatch("show/updateAspectRatio", value);
        }
      }
    },
    privacySelected: {
      get() {
        return this.show.privacySelected;
      },
      set(value) {
        if (value != this.privacySelected && value != "password") {
          this.showPasswordForm = false;
          this.$store.dispatch("show/updatePrivacy", value);
        } else {
          this.showPasswordForm = true;
        }
      }
    },
    unsavedChanges() {
      if (this.showPasswordForm && this.password != this.show.password) {
        return true;
      }
      if (this.showPasswordForm && this.privacySelected != "password") {
        return true;
      }
      return false;
    }
  },
  watch: {
    show(value) {
      this.password = value.password || "";

      // This is a bit tricky. We don't want to update the color in the component IF
      // the new color is equal hex of the new color ...
      // The user could enter "red" but the data store is #ff0000.
      var globalSlideBackgroundColor =
        value.data.displaySettings.globalSlideBackgroundColor || "#000000";
      if (
        tinycolor(globalSlideBackgroundColor).toHexString() !=
        tinycolor(this.globalSlideBackgroundColor).toHexString()
      ) {
        this.globalSlideBackgroundColor = globalSlideBackgroundColor;
      }

      var globalBackgroundColor =
        value.data.displaySettings.globalBackgroundColor || "#111111";
      if (
        tinycolor(globalBackgroundColor).toHexString() !=
        tinycolor(this.globalBackgroundColor).toHexString()
      ) {
        this.globalBackgroundColor = globalBackgroundColor;
      }
    },
    globalBackgroundColor(value) {
      console.log("watch", value);
      if (this.validateColor(value) != false) {
        var colors = {
          globalBackgroundColor: tinycolor(value).toHexString(),
          globalSlideBackgroundColor: tinycolor(
            this.globalSlideBackgroundColor
          ).toHexString()
        };
        this.$store.dispatch("show/updateColors", colors);
      }
    },
    globalSlideBackgroundColor(value) {
      console.log("watch", value);
      if (this.validateColor(value) != false) {
        var colors = {
          globalBackgroundColor: tinycolor(
            this.globalBackgroundColor
          ).toHexString(),
          globalSlideBackgroundColor: tinycolor(value).toHexString()
        };
        this.$store.dispatch("show/updateColors", colors);
      }
    }
  },
  mounted() {
    this.password = this.show.password || "";
    this.showPasswordForm = this.privacySelected == "password";
    var displaySettings = this.show.data.displaySettings || {};
    this.globalSlideBackgroundColor =
      displaySettings.globalSlideBackgroundColor || "#000000";
    this.globalBackgroundColor =
      displaySettings.globalBackgroundColor || "#111111";
  },
  methods: {
    validateColor(color) {
      var colorObj = tinycolor(color);
      if (colorObj.isValid()) {
        return null;
      } else {
        return false;
      }
    },
    savePassword() {
      this.$store.dispatch("show/updatePrivacyPassword", this.password);
    }
  }
};
</script>

<style lang="css" scoped></style>
